import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import toast from 'react-hot-toast';

interface Coupon {
  id: string;
  code: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
  is_downloaded: boolean;
  user_id: string | null;
}

export default function Coupons() {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [purchasedCoupons, setPurchasedCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showPaymentSiteModal, setShowPaymentSiteModal] = useState(false);
  const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] = useState(false);
  const [showActiveCouponModal, setShowActiveCouponModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [activeCouponExpiry, setActiveCouponExpiry] = useState<string | null>(null);
  const user = getCurrentUser();
  const navigate = useNavigate();

  useEffect(() => {
    loadCoupons();
    checkActiveCoupon();
  }, []);

  const checkActiveCoupon = async () => {
    if (!user) return;

    try {
      const { data: activeCoupons, error } = await supabase
        .from('stripecouponusage')
        .select('valid_until')
        .eq('user_id', user.id)
        .gte('valid_until', new Date().toISOString());

      if (error) {
        throw error;
      }

      if (activeCoupons && activeCoupons.length > 0) {
        setActiveCouponExpiry(activeCoupons[0].valid_until);
      } else {
        setActiveCouponExpiry(null);
      }
    } catch (error) {
      console.error('Error checking active coupon:', error);
    }
  };

  const loadCoupons = async () => {
    try {
      // Load available coupons
      const { data: availableCoupons, error: availableError } = await supabase
        .from('stripecoupons')
        .select('*')
        .is('user_id', null)
        .order('created_at', { ascending: false });

      if (availableError) throw availableError;

      // Load user's purchased coupons
      const { data: userCoupons, error: userError } = await supabase
        .from('stripecoupons')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (userError) throw userError;

      setCoupons(availableCoupons || []);
      setPurchasedCoupons(userCoupons || []);
    } catch (error: any) {
      toast.error('Error loading coupons');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (coupon: Coupon) => {
    if (!user) {
      toast.error('Please log in to purchase coupons');
      return;
    }

    // Check if user already has an active coupon
    if (activeCouponExpiry) {
      setSelectedCoupon(coupon);
      setShowActiveCouponModal(true);
      return;
    }

    setProcessing(true);
    try {
      // Get user's current balance
      const { data: userData } = await supabase
        .from('stripeusers')
        .select('wallet_balance')
        .eq('id', user.id)
        .single();

      if (!userData) throw new Error('User not found');
      if (userData.wallet_balance < coupon.price) {
        setSelectedCoupon(coupon);
        setShowInsufficientBalanceModal(true);
        setProcessing(false);
        return;
      }

      // Calculate validity period (30 days from now)
      const currentDate = new Date();
      const validUntil = new Date(currentDate);
      validUntil.setDate(validUntil.getDate() + 30); // Add 30 days

      // Update coupon ownership
      const { error: couponError } = await supabase
        .from('stripecoupons')
        .update({ user_id: user.id })
        .eq('id', coupon.id);

      if (couponError) throw couponError;

      // Create or update coupon usage
      const { error: usageError } = await supabase
        .from('stripecouponusage')
        .insert({
          user_id: user.id,
          coupon_id: coupon.id,
          valid_until: validUntil.toISOString()
        });

      if (usageError) throw usageError;

      // Update user's balance
      const { error: updateError } = await supabase
        .from('stripeusers')
        .update({ 
          wallet_balance: userData.wallet_balance - coupon.price 
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Create withdraw transaction
      const { error: transactionError } = await supabase
        .from('stripetransactions')
        .insert({
          user_id: user.id,
          amount: coupon.price,
          type: 'withdraw',
          status: 'completed'
        });

      if (transactionError) throw transactionError;

      // Update local state
      setActiveCouponExpiry(validUntil.toISOString());

      // Navigate to confirmation page
      navigate('/coupon-confirmation', { 
        state: { 
          coupon: {
            code: coupon.code,
            name: coupon.name,
            description: coupon.description,
            price: coupon.price
          }
        }
      });
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = async (coupon: Coupon) => {
    try {
      // Update download status
      const { error } = await supabase
        .from('stripecoupons')
        .update({ is_downloaded: true })
        .eq('id', coupon.id);

      if (error) throw error;

      // Create download link
      const element = document.createElement('a');
      const file = new Blob([
        `Your Song Access Coupon\n\n` +
        `Code: ${coupon.code}\n` +
        `Valid for: ${coupon.name}\n` +
        `${coupon.description}\n\n` +
        `This coupon provides 15 days of unlimited access to all songs and learning materials.\n\n` +
        `Your access has been automatically activated.\n` +
        `You can now enjoy learning languages through music!\n\n` +
        `Need help? Contact support@amalie.com`
      ], { type: 'text/plain' });
      
      element.href = URL.createObjectURL(file);
      element.download = `song-access-coupon-${coupon.code}.txt`;
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);

      toast.success('Coupon downloaded successfully!');
      loadCoupons(); // Reload to update download status
    } catch (error: any) {
      toast.error('Error downloading coupon');
    }
  };

  const handleTopUp = () => {
    navigate('/wallet');
  };

  const formatExpiryDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-600">Loading coupons...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Song Access Coupons</h1>
            <p className="mt-1 text-sm text-gray-500">
              Purchase coupons to access all songs and learning materials
            </p>
          </div>
          <Link
            to="/dashboard"
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Dashboard
          </Link>
        </div>

        {/* Available Coupons */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Available Coupons</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coupons.map((coupon) => (
              <div key={coupon.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="h-48 w-full overflow-hidden bg-gray-200">
                  <div className="w-full h-full bg-gradient-to-br from-gray-900 via-black to-gray-800 relative overflow-hidden flex items-center justify-center">
                    {/* Animated Coins */}
                    {[...Array(8)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center text-black font-bold text-sm shadow-lg animate-float-coin"
                        style={{
                          left: `${15 + (i % 4) * 20}%`,
                          top: `${20 + Math.floor(i / 4) * 30}%`,
                          animationDelay: `${i * 0.3}s`,
                          animationDuration: `${3 + (i % 3)}s`
                        }}
                      >
                        $
                      </div>
                    ))}
                    
                    {/* Central Glow Effect */}
                    <div className="absolute inset-0 bg-gradient-radial from-yellow-400/20 via-transparent to-transparent animate-pulse"></div>
                    
                    {/* Floating Sparkles */}
                    {[...Array(12)].map((_, i) => (
                      <div
                        key={`sparkle-${i}`}
                        className="absolute w-1 h-1 bg-yellow-300 rounded-full animate-sparkle"
                        style={{
                          left: `${Math.random() * 100}%`,
                          top: `${Math.random() * 100}%`,
                          animationDelay: `${Math.random() * 2}s`,
                          animationDuration: `${2 + Math.random() * 2}s`
                        }}
                      />
                    ))}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{coupon.name}</h3>
                  <p className="text-gray-600 mb-4">{coupon.description}</p>
                  <div className="mb-4">
                    <span className="text-lg font-mono bg-gray-100 px-3 py-1 rounded">
                      {coupon.code.substring(0, 2)}xxxxxx
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-2xl font-bold text-gray-900">
                      ${coupon.price.toFixed(2)}
                    </p>
                    <button
                      onClick={() => handlePurchase(coupon)}
                      disabled={processing}
                      className="bg-black text-white px-4 py-2 rounded-md hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black disabled:opacity-50"
                    >
                      {processing ? 'Processing...' : 'Buy Now'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Purchased Coupons */}
        {purchasedCoupons.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Coupons</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {purchasedCoupons.map((coupon) => (
                <div key={coupon.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
                  <div className="h-48 w-full overflow-hidden bg-gray-200">
                    <img
                      src={coupon.image_url}
                      alt={coupon.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{coupon.name}</h3>
                    <p className="text-gray-600 mb-4">{coupon.description}</p>
                    <div className="mb-4">
                      <span className="text-lg font-mono bg-gray-100 px-3 py-1 rounded">
                        {coupon.code}
                      </span>
                    </div>
                    <button
                      onClick={() => handleDownload(coupon)}
                      disabled={coupon.is_downloaded}
                      className={`w-full py-2 px-4 rounded-md ${
                        coupon.is_downloaded
                          ? 'bg-gray-200 text-gray-600 cursor-not-allowed'
                          : 'bg-black text-white hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black'
                      }`}
                    >
                      {coupon.is_downloaded ? 'Downloaded' : 'Download Coupon'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Coupon Modal */}
        {showActiveCouponModal && selectedCoupon && activeCouponExpiry && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">Active Coupon Found</h3>
              </div>
              <div className="p-6">
                <div className="mb-6">
                  <div className="flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mx-auto mb-4">
                    <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-600 text-center mb-4">
                    You already have an active coupon that provides access to all content.
                  </p>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Current coupon expires:</span>
                      <span className="font-semibold text-gray-900">
                        {formatExpiryDate(activeCouponExpiry)}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-4 text-center">
                    You can purchase a new coupon after your current one expires.
                  </p>
                </div>
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() => setShowActiveCouponModal(false)}
                    className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setShowActiveCouponModal(false);
                      navigate('/dashboard');
                    }}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200"
                  >
                    Go to Dashboard
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Insufficient Balance Modal */}
        {showInsufficientBalanceModal && selectedCoupon && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">Insufficient Balance</h3>
              </div>
              <div className="p-6">
                <div className="mb-6">
                  <p className="text-gray-600">
                    Your wallet balance is insufficient to purchase this coupon. Would you like to add funds to your wallet?
                  </p>
                  <p className="mt-2 text-sm text-gray-500">
                    Coupon price: ${selectedCoupon.price.toFixed(2)}
                  </p>
                </div>
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => setShowInsufficientBalanceModal(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-900"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleTopUp}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Add Money
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}